import { useCallback, useRef } from 'react';
import { useInitialLoadingScreenUIStore } from '../store/initialLoadingScreenUIStore';
import { logger } from '../utils/logger';

/**
 * Custom hook for managing initial loading screen state
 * This provides a more React-friendly way to interact with the initial loading screen store
 * rather than directly accessing the store in useEffect blocks
 */
export function useInitialLoadingScreenManager() {
  const loadingScreenStore = useInitialLoadingScreenUIStore();

  // Use ref to track if hideLoadingScreen has been called
  const hasHiddenScreenRef = useRef<boolean>(false);

  // Wrapper for hideLoadingScreen that ensures it's only called once
  // This should ONLY be called by topic components when they finish loading!
  const markRouteComplete = useCallback(() => {
    if (!hasHiddenScreenRef.current) {
      logger.component('Topic content fully loaded, hiding loading screen');
      hasHiddenScreenRef.current = true;
      loadingScreenStore.hideLoadingScreen();
    }
  }, [loadingScreenStore]);

  return {
    markRouteComplete,
    // Expose current loading state
    isVisible: loadingScreenStore.isVisible,
  };
}

// Export a function to directly access the store state
// This prevents invalid hook calls when used outside of components
export function getInitialLoadingScreenState() {
  const { isVisible } = useInitialLoadingScreenUIStore.getState();
  return {
    isVisible,
    // Add an empty Set to maintain compatibility with existing code
    loadedRoutes: new Set<string>(),
  };
}

// Simple initialization function for the loading screen
export function initializeLoadingScreen() {
  const currentPath = window.location.pathname;
  const store = useInitialLoadingScreenUIStore.getState();

  // For landing page, keep loading screen hidden
  if (currentPath === '/' || currentPath === '') {
    logger.component('Landing page detected at startup, leaving loading screen hidden');
    return;
  }

  // For auth routes, keep loading screen hidden (they handle their own UI state)
  if (currentPath.startsWith('/auth/')) {
    logger.component('Auth route detected at startup, leaving loading screen hidden');
    return;
  }

  // For authenticated routes and other app pages, show the loading screen
  logger.component('Non-landing page detected at startup, showing loading screen');
  store.showLoadingScreen();
}
